import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, ErrorState, PageHead, Pill, ROLE_LABEL, Skeleton } from '@fa/ui';
import { ApiError, api } from '../lib/api.js';
import type { TeamMember } from '../lib/types.js';

export function Team(): ReactNode {
  const queryClient = useQueryClient();
  const [convidando, setConvidando] = useState(false);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState('operador');
  const [aviso, setAviso] = useState<string | null>(null);

  const convidar = useMutation({
    mutationFn: () =>
      api<{ message: string }>('/v1/team', {
        method: 'POST',
        body: { name: nome, email, role: papel },
      }),
    onSuccess: (data) => {
      setAviso(data.message);
      setConvidando(false);
      setNome('');
      setEmail('');
      void queryClient.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (err) => setAviso(err instanceof ApiError ? err.message : 'Não foi possível convidar.'),
  });

  const remover = useMutation({
    mutationFn: (id: string) => api(`/v1/team/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setAviso('Acesso removido.');
      void queryClient.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (err) => setAviso(err instanceof ApiError ? err.message : 'Não foi possível remover.'),
  });

  const query = useQuery({
    queryKey: ['team'],
    queryFn: () => api<{ data: TeamMember[] }>('/v1/team'),
  });

  return (
    <>
      <PageHead
        title="Equipe"
        subtitle="Quem acessa este portal e o que pode fazer"
        actions={
          <button className="btn btn-primary" onClick={() => setConvidando((v) => !v)}>
            {convidando ? 'Cancelar' : 'Convidar pessoa'}
          </button>
        }
      />

      {aviso && (
        <div className="alert info">
          <b>Resultado</b>
          {aviso}
          <div className="fix">
            <button className="btn btn-sm btn-ghost" onClick={() => setAviso(null)}>
              Fechar
            </button>
          </div>
        </div>
      )}

      {convidando && (
        <Card title="Novo acesso">
          <div style={{ display: 'grid', gap: 14 }}>
            <div className="grid-3">
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--ink-2)' }}>Nome</span>
                <input className="input" value={nome} onChange={(e) => setNome(e.target.value)} />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--ink-2)' }}>E-mail</span>
                <input
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--ink-2)' }}>Papel</span>
                <select className="input" value={papel} onChange={(e) => setPapel(e.target.value)}>
                  <option value="admin">{ROLE_LABEL.admin}</option>
                  <option value="financeiro">{ROLE_LABEL.financeiro}</option>
                  <option value="operador">{ROLE_LABEL.operador}</option>
                  <option value="leitura">{ROLE_LABEL.leitura}</option>
                </select>
              </label>
            </div>
            <div style={{ color: 'var(--ink-3)', fontSize: '.85rem' }}>
              A pessoa recebe uma senha provisória por e-mail e a troca no primeiro acesso.
            </div>
            <div>
              <button
                className="btn btn-primary"
                disabled={nome.length < 2 || !email.includes('@') || convidar.isPending}
                onClick={() => convidar.mutate()}
              >
                {convidar.isPending ? 'Enviando convite...' : 'Enviar convite'}
              </button>
            </div>
          </div>
        </Card>
      )}

      <Card padded={false}>
        {query.isLoading && <Skeleton rows={3} />}
        {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
        {query.data && (
          <table>
            <thead>
              <tr>
                <th>Pessoa</th>
                <th>Papel</th>
                <th>Autenticação em 2 etapas</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {query.data.data.map((member) => (
                <tr key={member.id}>
                  <td>
                    <b>{member.name}</b>
                    <span className="sub">{member.email}</span>
                  </td>
                  <td>{ROLE_LABEL[member.role] ?? member.role}</td>
                  <td>
                    <Pill tone={member.mfa_enabled ? 'ok' : 'warn'}>
                      {member.mfa_enabled ? 'Ativa' : 'Pendente'}
                    </Pill>
                  </td>
                  <td className="r">
                    {member.role !== 'owner' && (
                      <button
                        className="btn btn-sm btn-ghost"
                        disabled={remover.isPending}
                        onClick={() => {
                          if (window.confirm(`Remover o acesso de ${member.name}?`)) {
                            remover.mutate(member.id);
                          }
                        }}
                      >
                        Remover
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
